import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Wallet, TrendingUp, DollarSign, ShoppingBag, AlertCircle } from "lucide-react";
import { categoryIcons } from "../lib/constants";
import { useApi } from "../hooks/useApi";
import { DashboardLayout } from "../components/DashboardLayout";

const StatCard = ({ label, value, sub, icon: Icon, color }) => (
  <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-5 border border-purple-500/20">
    <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center mb-3`}>
      <Icon className="w-4 h-4 text-white" />
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
    <p className="text-xs text-gray-400">{label}</p>
    {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
  </div>
);

export const EarningsPage = () => {
  const api = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const res = await api.get("/earnings");
      setData(res.data);
    } catch (e) {
      toast.error("Failed to load earnings");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
        </div>
      </DashboardLayout>
    );
  }

  const maxMonth = Math.max(...(data?.monthly_trend || []).map((m) => m.amount), 1);

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto" data-testid="earnings-page">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white">Earnings</h1>
          <p className="text-gray-400 mt-1">Track your income from selling data on the marketplace</p>
        </div>

        {/* Available balance banner */}
        <div className="bg-gradient-to-r from-purple-600/30 to-cyan-500/20 rounded-2xl p-6 border border-purple-500/30 mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <p className="text-sm text-gray-300">Available Balance</p>
            <p className="text-4xl font-bold text-white mt-1" data-testid="available-balance">${data.available_balance.toFixed(2)}</p>
            <p className="text-xs text-gray-400 mt-1">After {(data.platform_fee_rate * 100).toFixed(0)}% platform fee</p>
          </div>
          <button
            onClick={() => toast.info("Payouts are simulated in this MVP. Connect a payout method to withdraw real funds.")}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-cyan-500 text-white rounded-xl font-semibold hover:shadow-lg transition"
            data-testid="withdraw-btn"
          >
            Withdraw Funds
          </button>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard label="Gross Earnings" value={`$${data.gross_earnings.toFixed(2)}`} icon={DollarSign} color="from-green-500 to-emerald-600" />
          <StatCard label="Net Earnings" value={`$${data.net_earnings.toFixed(2)}`} sub={`-$${data.platform_fees.toFixed(2)} fees`} icon={Wallet} color="from-purple-500 to-violet-600" />
          <StatCard label="Total Sales" value={data.total_sales} icon={ShoppingBag} color="from-blue-500 to-cyan-500" />
          <StatCard label="Avg. Sale" value={`$${data.average_sale.toFixed(2)}`} icon={TrendingUp} color="from-yellow-500 to-orange-500" />
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          {/* Monthly trend */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20">
            <h2 className="text-lg font-semibold text-white mb-4">Monthly Earnings</h2>
            {data.gross_earnings === 0 ? (
              <p className="text-gray-500 text-sm">No earnings yet. List your data to start earning.</p>
            ) : (
              <>
                <div className="h-40 flex items-end justify-around gap-2" data-testid="earnings-trend">
                  {data.monthly_trend.map((m) => (
                    <div
                      key={m.month}
                      className="flex-1 bg-gradient-to-t from-purple-600 to-cyan-400 rounded-t-lg min-h-[4px] transition-all"
                      style={{ height: `${Math.max((m.amount / maxMonth) * 100, 3)}%` }}
                      title={`${m.month}: $${m.amount.toFixed(2)}`}
                    />
                  ))}
                </div>
                <div className="flex justify-around gap-2 mt-2">
                  {data.monthly_trend.map((m) => (
                    <span key={m.month} className="flex-1 text-center text-xs text-gray-500">{m.month.slice(5)}</span>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Earnings by category */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20">
            <h2 className="text-lg font-semibold text-white mb-4">Earnings by Category</h2>
            {data.earnings_by_category.length === 0 ? (
              <p className="text-gray-500 text-sm">No category data yet.</p>
            ) : (
              <div className="space-y-3">
                {data.earnings_by_category.map((c) => {
                  const Icon = categoryIcons[c.category] || DollarSign;
                  const pct = (c.amount / data.gross_earnings) * 100;
                  return (
                    <div key={c.category}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="flex items-center gap-2 text-sm text-white capitalize">
                          <Icon className="w-4 h-4 text-purple-400" /> {c.category}
                        </span>
                        <span className="text-sm text-cyan-400">${c.amount.toFixed(2)}</span>
                      </div>
                      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-purple-600 to-cyan-400" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Recent sales */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20">
          <h2 className="text-lg font-semibold text-white mb-4">Recent Sales</h2>
          {data.recent_sales.length === 0 ? (
            <div className="text-center py-8">
              <Wallet className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">No sales yet. Once buyers purchase your data, your earnings will appear here.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="recent-sales">
              {data.recent_sales.map((s) => (
                <div key={s.id} className="flex items-center justify-between bg-white/5 rounded-xl px-4 py-3">
                  <div>
                    <p className="text-white font-medium flex items-center gap-2">
                      {s.listing_title}
                      {s.flagged && <span title="Flagged for review"><AlertCircle className="w-4 h-4 text-yellow-400" /></span>}
                    </p>
                    <p className="text-xs text-gray-400">to {s.buyer_name} • {new Date(s.date).toLocaleDateString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-green-400 font-bold">+${s.net_amount.toFixed(2)}</p>
                    <p className="text-xs text-gray-500">${s.amount.toFixed(2)} gross</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};
